import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";

// Public pages
import Home from "@/pages/Home";
import About from "@/pages/About";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Sectors from "@/pages/Sectors";
import WhyUs from "@/pages/WhyUs";
import Governance from "@/pages/Governance";
import Contact from "@/pages/Contact";
import CookiePolicy from "@/pages/CookiePolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfUse from "@/pages/TermsOfUse";
import NotFound from "@/pages/NotFound";

// Public layout components
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import WhatsAppFAB from "@/components/WhatsAppFAB";

// Admin
import { AdminAuthProvider } from "@/admin/AdminAuthContext";
import ProtectedAdminRoute from "@/admin/ProtectedAdminRoute";
import AdminLogin from "@/admin/pages/AdminLogin";
import AdminDashboard from "@/admin/pages/AdminDashboard";
import AdminChangePassword from "@/admin/pages/AdminChangePassword";
import AdminSubsidiaries from "@/admin/pages/AdminSubsidiaries";
import AdminTestimonials from "@/admin/pages/AdminTestimonials";
import AdminClients from "@/admin/pages/AdminClients";
import AdminSubmissions from "@/admin/pages/AdminSubmissions";
import AdminMedia from "@/admin/pages/AdminMedia";
import AdminPages from "@/admin/pages/AdminPages";
import AdminSettings from "@/admin/pages/AdminSettings";

function PublicLayout({ children }) {
  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieConsent />
      <WhatsAppFAB />
    </div>
  );
}

function RootShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {isAdmin ? (
        <AdminAuthProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/change-password" element={<ProtectedAdminRoute><AdminChangePassword /></ProtectedAdminRoute>} />
            <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="/admin/subsidiaries" element={<ProtectedAdminRoute><AdminSubsidiaries /></ProtectedAdminRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedAdminRoute><AdminTestimonials /></ProtectedAdminRoute>} />
            <Route path="/admin/clients" element={<ProtectedAdminRoute><AdminClients /></ProtectedAdminRoute>} />
            <Route path="/admin/submissions" element={<ProtectedAdminRoute><AdminSubmissions /></ProtectedAdminRoute>} />
            <Route path="/admin/media" element={<ProtectedAdminRoute><AdminMedia /></ProtectedAdminRoute>} />
            <Route path="/admin/pages" element={<ProtectedAdminRoute><AdminPages /></ProtectedAdminRoute>} />
            <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
            <Route path="/admin/*" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
          </Routes>
        </AdminAuthProvider>
      ) : (
        <PublicLayout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:slug" element={<CompanyDetail />} />
            <Route path="/sectors" element={<Sectors />} />
            <Route path="/why-us" element={<WhyUs />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PublicLayout>
      )}
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <RootShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
